import { useState, useEffect, useCallback } from 'react';

const POLL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Finnhub API token — reads from environment variable (set in Cloudflare Pages),
 * falls back to the hardcoded key for local development.
 */
const FINNHUB_TOKEN =
  process.env.REACT_APP_FINNHUB_KEY ||
  'd800iu9r01qj3ct8kgigd800iu9r01qj3ct8kgj0';

const FINNHUB_BASE = 'https://finnhub.io/api/v1';

/**
 * Fetch current price + 52-week high for one ticker via Finnhub.
 *
 * Two requests are made in parallel:
 *   /quote            → current price  (field "c")
 *   /stock/metric     → 52-week high   (field metric["52WeekHigh"])
 *
 * The metric call is best-effort; if it fails, ath is returned as null.
 * Works for stocks, ETFs, and indices (use "^VIX" for VIX).
 */
function toFinnhubSymbol(ticker) {
  const map = { 'NQSE.DE': 'XETRA:NQSE', 'IH2O.MI': 'MIL:IH2O' };
  return map[ticker.toUpperCase()] ?? ticker;
}
async function fetchOneTicker(ticker) {
  const sym = encodeURIComponent(toFinnhubSymbol(ticker));
  const tok = encodeURIComponent(FINNHUB_TOKEN);

  const [quoteRes, metricRes] = await Promise.allSettled([
    fetch(`${FINNHUB_BASE}/quote?symbol=${sym}&token=${tok}`),
    fetch(`${FINNHUB_BASE}/stock/metric?symbol=${sym}&metric=52Week&token=${tok}`),
  ]);

  if (quoteRes.status !== 'fulfilled' || !quoteRes.value.ok) {
    throw new Error(`Quote fetch failed for ${ticker}`);
  }
  const quote = await quoteRes.value.json();

  const price = quote.c;
  if (!price || price === 0) throw new Error(`No price data for ${ticker}`);

  let ath = null;
  if (metricRes.status === 'fulfilled' && metricRes.value.ok) {
    try {
      const metric = await metricRes.value.json();
      ath = metric?.metric?.['52WeekHigh'] ?? null;
    } catch { /* ignore parse errors */ }
  }

  return { price, ath };
}

/**
 * Poll Finnhub for a list of tickers every 5 minutes.
 *
 * @param {string[]} tickers  Uppercase ticker symbols to watch.
 *                            Supports stocks, ETFs, and indices like '^VIX'.
 * @returns {{ data: Object, getStockData: (ticker: string) => StockEntry }}
 *
 * StockEntry = { price?: number, ath?: number, updatedAt?: Date,
 *                status: 'loading' | 'live' | 'offline' }
 */
export function useStockFeed(tickers) {
  const tickersKey = [...new Set(tickers.map(t => t.toUpperCase()))].sort().join(',');

  const [data, setData] = useState(() => {
    const init = {};
    tickers.forEach(t => { init[t.toUpperCase()] = { status: 'loading' }; });
    return init;
  });

  const fetchAll = useCallback(async (tickerList) => {
    if (tickerList.length === 0) return;

    const results = await Promise.allSettled(
      tickerList.map(t => fetchOneTicker(t).then(d => ({ t, ...d })))
    );

    setData(prev => {
      const next = { ...prev };
      results.forEach((r, i) => {
        const t = tickerList[i];
        if (r.status === 'fulfilled') {
          next[t] = {
            price:     r.value.price,
            ath:       r.value.ath,
            updatedAt: new Date(),
            status:    'live',
          };
        } else {
          next[t] = { ...(prev[t] ?? {}), status: 'offline' };
        }
      });
      return next;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!tickersKey) return;
    const current = tickersKey.split(',');

    setData(prev => {
      const next = { ...prev };
      let changed = false;
      current.forEach(t => {
        if (!next[t]) { next[t] = { status: 'loading' }; changed = true; }
      });
      return changed ? next : prev;
    });

    fetchAll(current);
    const timer = setInterval(() => fetchAll(current), POLL_MS);
    return () => clearInterval(timer);
  }, [tickersKey, fetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  function getStockData(ticker) {
    return data[ticker?.toUpperCase()] ?? { status: 'loading' };
  }

  return { data, getStockData };
}
